import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Fade,
  Slide,
  Zoom,
  alpha,
  useTheme,
  useMediaQuery,
  styled,
  keyframes,
  Badge,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Science,
  Assignment,
  CheckCircle,
  Warning,
  AccessTime,
  Analytics,
  Visibility,
  Schedule,
  TrendingUp,
  Speed,
  Groups,
  Biotech,
  VerifiedUser,
  Engineering,
  Science as ChemicalEngineering,
  LocalShipping,
  Dashboard as DashboardIcon,
  Timeline,
  AutoAwesome,
  Close,
  Insights,
  Assignment as Certificate,
  Hub,
  ScatterPlot,
  Grain,
  Opacity,
  FilterVintage,
  Spa,
  // Professional Laboratory Icons
  Inventory2,
  PrecisionManufacturing,
  Psychology,
  Whatshot,
  TrendingUp as ICPIcon,
  Diamond,
  InvertColors,
  GppGood,
  MenuBook,
  TaskAlt,
  Build,
  LocalPharmacy,
  Flare,
  AutoGraph,
  Stars,
  WaterDrop,
  // Enhanced Professional Icons
  AccountTree,
  Timeline as TimelineIcon,
  Layers,
  CallSplit,
  DeviceHub,
  Radar,
  Schema,
  Polyline,
  TrendingUp as TrendingUpIcon,
  BarChart,
  ShowChart,
  MultilineChart,
  CandlestickChart,
  BubbleChart,
  PieChart,
  DonutSmall,
  DataUsage,
  QueryStats,
  MonitorHeart,
  NetworkCheck,
  Speed as SpeedIcon,
  FlashOn,
  Bolt,
  ElectricBolt,
  Celebration,
  EmojiEvents,
  WorkspacePremium,
  Verified,
  Security,
  Shield,
  Lock,
  VpnKey,
  MilitaryTech,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { clientDashboardService } from '../services/clientDashboardService';
import samplesService, { SampleStatus } from '../services/samplesService';

// Advanced animations for the network
const particleFlow = keyframes`
  0% { 
    transform: translateX(-50px) translateY(0px); 
    opacity: 0; 
    scale: 0.5;
  }
  50% { 
    opacity: 1; 
    scale: 1;
    filter: brightness(1.5);
  }
  100% { 
    transform: translateX(50px) translateY(0px); 
    opacity: 0; 
    scale: 0.5;
  }
`;

const networkPulse = keyframes`
  0% { 
    transform: scale(1) rotate(0deg); 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
  50% { 
    transform: scale(1.1) rotate(2deg); 
    box-shadow: 0 0 40px rgba(37, 99, 235, 0.6);
  }
  100% { 
    transform: scale(1) rotate(0deg); 
    box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  }
`;

const badgePulse = keyframes`
  0% { 
    transform: scale(1); 
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% { 
    transform: scale(1.1); 
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% { 
    transform: scale(1); 
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`;

// Responsive Sankey Container
const SankeyContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '600px',
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.95)} 0%, 
    ${alpha(theme.palette.primary.main, 0.02)} 50%, 
    ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
  borderRadius: theme.spacing(3),
  overflow: 'auto', // Allow scrolling on mobile
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  backdropFilter: 'blur(20px)',
  
  // Mobile responsive
  [theme.breakpoints.down('md')]: {
    height: '400px',
    overflow: 'auto',
    borderRadius: theme.spacing(2),
  },
  
  [theme.breakpoints.down('sm')]: {
    height: '700px', // Increased height for wider vertical flow
    margin: '0', // Remove negative margins for wider content
    borderRadius: theme.spacing(2),
    overflow: 'auto',
    width: 'calc(100% - 8px)', // Use almost full width
  },
}));

// Enhanced Interactive Card with better sizing and mobile support
const InteractiveCard = styled(Box, {
  shouldForwardProp: (prop) => !['active', 'nodeType', 'laboratory', 'sampleCount', 'isMobile'].includes(prop as string),
})<{ 
  active?: boolean; 
  nodeType?: 'reception' | 'preparation' | 'laboratory' | 'validation' | 'certificate' | 'complete';
  laboratory?: string;
  sampleCount?: number;
  isMobile?: boolean;
}>(({ theme, active, nodeType, laboratory, sampleCount = 0, isMobile = false }) => {
  const getAdvancedNodeColor = () => {
    switch (nodeType) {
      case 'reception': return { 
        main: '#3b82f6', 
        dark: '#1d4ed8', 
        accent: '#60a5fa',
        glow: '#bfdbfe' 
      };
      case 'preparation': return { 
        main: '#7c3aed', 
        dark: '#5b21b6', 
        accent: '#a78bfa',
        glow: '#ddd6fe' 
      };
      case 'laboratory': 
        switch (laboratory) {
          case 'SAA': return { 
            main: '#dc2626', 
            dark: '#991b1b', 
            accent: '#f87171',
            glow: '#fecaca' 
          };
          case 'ICP': return { 
            main: '#059669', 
            dark: '#047857', 
            accent: '#34d399',
            glow: '#a7f3d0' 
          };
          case 'AU': return { 
            main: '#d97706', 
            dark: '#92400e', 
            accent: '#fb923c',
            glow: '#fde68a' 
          };
          case 'VOLUMETRIE': return { 
            main: '#7c2d12', 
            dark: '#451a03', 
            accent: '#fb923c',
            glow: '#fed7aa' 
          };
          default: return { 
            main: '#6b7280', 
            dark: '#374151', 
            accent: '#9ca3af',
            glow: '#d1d5db' 
          };
        }
      case 'validation': return { 
        main: '#ea580c', 
        dark: '#c2410c', 
        accent: '#fb923c',
        glow: '#fed7aa' 
      };
      case 'certificate': return { 
        main: '#0891b2', 
        dark: '#0e7490', 
        accent: '#22d3ee',
        glow: '#a5f3fc' 
      };
      case 'complete': return { 
        main: '#16a34a', 
        dark: '#15803d', 
        accent: '#4ade80',
        glow: '#bbf7d0' 
      };
      default: return { 
        main: '#6b7280', 
        dark: '#374151', 
        accent: '#9ca3af',
        glow: '#d1d5db' 
      };
    }
  };

  const colors = getAdvancedNodeColor();
  const intensity = Math.min(sampleCount / 10, 1);
  const isActive = active || sampleCount > 0;
  
  // Responsive sizing
  const cardWidth = isMobile ? '80px' : '120px';
  const cardHeight = isMobile ? '50px' : '70px';
  const borderRadius = isMobile ? '16px' : '20px';
  
  return {
    position: 'absolute',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: cardWidth,
    height: cardHeight,
    borderRadius: borderRadius,
    background: `
      radial-gradient(circle at 30% 30%, ${alpha(colors.accent, 0.4)}, transparent 70%),
      radial-gradient(circle at 70% 70%, ${alpha(colors.glow, 0.3)}, transparent 60%),
      linear-gradient(135deg, ${colors.main} 0%, ${colors.dark} 40%, ${colors.main} 100%)
    `,
    border: isActive 
      ? `3px solid ${colors.accent}` 
      : `2px solid ${alpha(colors.accent, 0.3)}`,
    color: theme.palette.common.white,
    transition: 'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
    transform: isActive ? (isMobile ? 'scale(1.05)' : 'scale(1.08)') : 'scale(1)',
    zIndex: isActive ? 30 : sampleCount > 0 ? 20 : 10,
    boxShadow: isActive 
      ? `
        0 0 50px ${alpha(colors.main, 0.8)},
        0 20px 60px ${alpha(colors.main, 0.4)},
        0 5px 15px ${alpha(colors.dark, 0.3)},
        inset 0 2px 0 ${alpha(theme.palette.common.white, 0.25)},
        inset 0 -2px 0 ${alpha(colors.dark, 0.3)}
      `
      : `
        0 8px 30px ${alpha(colors.main, intensity * 0.5)},
        0 4px 15px ${alpha(colors.main, 0.25)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.15)}
      `,
    backdropFilter: 'blur(20px)',
    overflow: 'visible', // Allow badges to show outside
    animation: isActive ? `${networkPulse} 2s ease-in-out infinite` : 'none',
    
    '&:hover': {
      transform: isActive ? (isMobile ? 'scale(1.1)' : 'scale(1.15)') : (isMobile ? 'scale(1.05)' : 'scale(1.08)'),
      zIndex: 40,
      boxShadow: `
        0 0 60px ${alpha(colors.main, 0.9)},
        0 25px 70px ${alpha(colors.main, 0.5)},
        0 10px 20px ${alpha(colors.dark, 0.4)}
      `,
    },
  };
});

const SankeyFlow = styled('svg')<{ active?: boolean; thickness?: number }>(({ theme, active, thickness = 1 }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 2,
  '& .flow-path': {
    fill: 'none',
    stroke: active ? theme.palette.warning.main : alpha(theme.palette.primary.main, 0.4),
    strokeWidth: Math.max(2, thickness * 20),
    opacity: active ? 0.8 : 0.6,
    transition: 'all 0.4s ease',
    filter: active ? `drop-shadow(0 0 8px ${theme.palette.warning.main})` : 'none',
  },
}));

// Enhanced Sample Count Badge
const SampleCountBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#f44336 !important',
    color: '#ffffff !important',
    fontWeight: 'bold !important',
    fontSize: '14px !important',
    minWidth: '32px !important',
    height: '32px !important',
    borderRadius: '16px !important',
    border: `3px solid ${theme.palette.common.white} !important`,
    boxShadow: '0 4px 12px rgba(244, 67, 54, 0.8) !important',
    animation: `${badgePulse} 2s infinite !important`,
    top: '-12px !important',
    right: '-12px !important',
    zIndex: '9999 !important',
    position: 'absolute !important',
    transform: 'none !important',
    
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px !important',
      minWidth: '28px !important',
      height: '28px !important',
      borderRadius: '14px !important',
      top: '-10px !important',
      right: '-10px !important',
    },
  },
}));

interface NetworkNodeData {
  id: string;
  name: string;
  shortName: string;
  icon: React.ReactNode;
  x: number;
  y: number;
  type: 'reception' | 'preparation' | 'laboratory' | 'validation' | 'certificate' | 'complete';
  laboratory?: string;
  stage?: string;
  count: number;
  connections: string[];
  description: string;
}

interface WorkflowStats {
  total_samples: number;
  reception: number;
  preparation: number;
  saa_prep: number;
  saa_analyse: number;
  icp_prep: number;
  icp_analyse: number;
  au_prep: number;
  au_analyse: number;
  vol_prep: number;
  vol_analyse: number;
  validation: number;
  certificate: number;
  completed: number;
}

interface ClientSample {
  id: string;
  sample_id: string;
  external_id?: string; // Add client reference
  client_name: string;
  status: string;
  priority: string;
  progress_percentage: number;
  workflow_duration_days: number;
  assigned_technician?: string;
  current_location?: string;
  metadonnees?: any; // Added for batch information
}

interface ClientWorkflowVisualizationProps {
  onStageClick?: (stageId: string, samples?: ClientSample[]) => void;
}

const ClientWorkflowVisualization: React.FC<ClientWorkflowVisualizationProps> = ({
  onStageClick,
}) => {
  const theme = useTheme();
  const sankeyRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<WorkflowStats | null>(null);
  const [selectedNode, setSelectedNode] = useState<NetworkNodeData | null>(null);
  const [stageSamples, setStageSamples] = useState<ClientSample[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const [activeConnections, setActiveConnections] = useState<Set<string>>(new Set());
  const [expandedBatches, setExpandedBatches] = useState<Set<string>>(new Set());
  const [actualValidationCounts, setActualValidationCounts] = useState<{[key: string]: number}>({});
  const [stageCounts, setStageCounts] = useState<Record<string, number>>({});

  // Batch grouping helper functions (like admin dashboards)
  const groupSamplesByBatch = (samples: ClientSample[]) => {
    const batches: Record<string, ClientSample[]> = {};
    const individualSamples: ClientSample[] = [];

    samples.forEach(sample => {
      const batchInfo = (sample as any).metadonnees?.batch_info || (sample as any).metadonnees?.batch_analysis_summary;
      if (batchInfo && (batchInfo.is_batch_sample || batchInfo.batch_number)) {
        const batchNumber = batchInfo.batch_number;
        if (!batches[batchNumber]) {
          batches[batchNumber] = [];
        }
        batches[batchNumber].push(sample);
      } else {
        individualSamples.push(sample);
      }
    });

    return { batches, individualSamples };
  };

  const isBatchSample = (sample: ClientSample): boolean => {
    const batchInfo = (sample as any).metadonnees?.batch_info || (sample as any).metadonnees?.batch_analysis_summary;
    return batchInfo?.is_batch_sample === true || !!batchInfo?.batch_number;
  };

  const getBatchNumber = (sample: ClientSample): string | null => {
    const batchInfo = (sample as any).metadonnees?.batch_info || (sample as any).metadonnees?.batch_analysis_summary;
    return batchInfo?.batch_number || null;
  };

  // Load actual validation counts using same logic as supervisor dashboards
  const loadActualValidationCounts = async () => {
    try {
      // 🔥 CRITICAL BGM FIX: Fetch all validation samples (CLIENT-SCOPED)
      // Note: clientDashboardService doesn't support array status, so we fetch all validation samples
      // and filter client-side for BGM samples from different statuses
      
      // First get VALIDATION_SERVICE samples
      const validationResponse = await clientDashboardService.getClientSamples(1, 1000, { 
        status: SampleStatus.VALIDATION_SERVICE 
      });
      
      // Then get ICP workflow samples for BGM validation
      const icpPrepResponse = await clientDashboardService.getClientSamples(1, 1000, { 
        status: SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE 
      });
      
      const icpAnalyseResponse = await clientDashboardService.getClientSamples(1, 1000, { 
        status: SampleStatus.LABORATOIRE_ICP_ANALYSE 
      });
      
      // Combine all samples
      const allValidationSamples = [
        ...(validationResponse.samples || []),
        ...(icpPrepResponse.samples || []),
        ...(icpAnalyseResponse.samples || [])
      ];
      
      const counts = { SAA: 0, ICP: 0, AU: 0, VOLUMETRIE: 0 };
      
      // Apply EXACT same logic as Admin Dashboard (but CLIENT-SCOPED)
      allValidationSamples.forEach((sample: any) => {
        const isBGMSample = sample.metadonnees?.bgm_method_selected === true;
        
        if (isBGMSample) {
          // BGM SAMPLE LOGIC - Same as Admin Dashboard
          const icpAnalysisComplete = sample.metadonnees?.icp_analysis_completed === true;
          const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
          const auValidationComplete = sample.metadonnees?.au_validation_completed === true;
          
          // ICP validation count for BGM
          // 🔥 FIX: BGM samples in ICP workflow (not just VALIDATION_SERVICE)
          if (icpAnalysisComplete && 
              (!icpValidationComplete || (icpValidationComplete && !auValidationComplete))) {
            counts.ICP++;
          }
          
          // AU validation count for BGM
          const auAnalysisComplete = sample.metadonnees?.au_analysis_completed === true;
          if (auAnalysisComplete && (!auValidationComplete || (auValidationComplete && !icpValidationComplete))) {
            counts.AU++;
          }
        } else {
          // NON-BGM SAMPLE LOGIC - Same as Admin Dashboard
          if (sample.statut === SampleStatus.VALIDATION_SERVICE) {
            if (sample.validation_source_laboratory) {
              // Use validation_source_laboratory if available
              switch (sample.validation_source_laboratory) {
                case 'LABORATOIRE_SAA': counts.SAA++; break;
                case 'LABORATOIRE_ICP': counts.ICP++; break;
                case 'LABORATOIRE_AU': counts.AU++; break;
                case 'LABORATOIRE_VOLUMETRIE': counts.VOLUMETRIE++; break;
              }
            } else {
              // Fallback to analysis form presence
              if (sample.metadonnees?.saa_analysis_form && !sample.metadonnees?.bgm_method_selected) counts.SAA++;
              if ((sample.metadonnees?.icp_analysis_form || sample.metadonnees?.icp_analysis_data) && !sample.metadonnees?.bgm_method_selected) counts.ICP++;
              if (sample.metadonnees?.au_analysis_form && !sample.metadonnees?.bgm_method_selected) counts.AU++;
              if (sample.metadonnees?.volumetrie_analysis_form && !sample.metadonnees?.bgm_method_selected) counts.VOLUMETRIE++;
            }
          }
        }
      });
      
      console.log('📊 Validation counts calculated:', counts);
      console.log(`   Total validation samples: ${allValidationSamples.length}`);
      setActualValidationCounts(counts);
    } catch (error) {
      console.error('Error loading validation counts:', error);
    }
  };

  // Load precise per-stage counts using server totals (client-scoped)
  // Using limit=1 for count-only queries to optimize performance
  const loadStageCounts = async () => {
    try {
      const makeReq = async (id: string, status: string, extra?: any) => {
        // Only fetch 1 sample to get the total count (optimized for high volume)
        const res = await clientDashboardService.getClientSamples(1, 1, { status, ...(extra || {}) });
        return { id, total: res.total };
      };
      const requests: Array<Promise<{ id: string; total: number }>> = [];
      requests.push(makeReq('reception', 'RECEPTION'));
      requests.push(makeReq('prep_mecanique', 'PREPARATION_MECANIQUE'));
      requests.push(makeReq('saa_prep', 'LABORATOIRE_SAA_PREP_CHIMIQUE'));
      requests.push(makeReq('icp_prep', 'LABORATOIRE_ICP_PREP_CHIMIQUE'));
      requests.push(makeReq('au_prep', 'LABORATOIRE_AU_PREP_CHIMIQUE', { laboratory: 'LABORATOIRE_AU' }));
      requests.push(makeReq('vol_prep', 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE'));
      requests.push(makeReq('saa_analyse', 'LABORATOIRE_SAA_ANALYSE'));
      requests.push(makeReq('icp_analyse', 'LABORATOIRE_ICP_ANALYSE'));
      requests.push(makeReq('au_analyse', 'LABORATOIRE_AU_ANALYSE', { laboratory: 'LABORATOIRE_AU' }));
      requests.push(makeReq('vol_analyse', 'LABORATOIRE_VOLUMETRIE_ANALYSE'));
      requests.push(makeReq('certificate', 'GENERATION_CERTIFICAT'));
      requests.push(makeReq('complete', 'PRET'));

      const results = await Promise.all(requests);
      const counts: Record<string, number> = {};
      results.forEach(r => { counts[r.id] = r.total; });
      console.log('📈 Stage counts loaded:', counts);
      setStageCounts(counts);
    } catch (e) {
      console.error('Error loading stage counts:', e);
      // ignore errors; UI will fallback to analytics counts
    }
  };

  // Helper function to get laboratory-specific validation counts
  const getValidationSamplesForLab = (laboratory: string): number => {
    // 🎯 PRIMARY: Use actual validation counts if available
    if (Object.keys(actualValidationCounts).length > 0) {
      return actualValidationCounts[laboratory] || 0;
    }
    
    // 🔧 FALLBACK: Use proportional distribution (temporary until counts load)
    const totalValidation = stats?.validation || 0;
    if (totalValidation === 0) return 0;
    
    // Distribute equally across 4 labs as fallback
    return Math.floor(totalValidation / 4);
  };

  const toggleBatchExpansion = (batchNumber: string) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchNumber)) {
      newExpanded.delete(batchNumber);
    } else {
      newExpanded.add(batchNumber);
    }
    setExpandedBatches(newExpanded);
  };

  // Responsive node positioning
  const getResponsivePositions = () => {
    if (isMobile) {
      // Mobile: Vertical flow with wider spacing
      return {
        scale: 0.9,
        containerWidth: '100%',
        positions: [
          // Vertical flow stages - centered and wider spread
          { x: 180, y: 40 },   // reception
          { x: 180, y: 130 },  // prep_mecanique
          // Laboratory preparation row - wider spacing
          { x: 30, y: 220 },   // saa_prep
          { x: 120, y: 220 },  // icp_prep
          { x: 240, y: 220 },  // au_prep
          { x: 330, y: 220 },  // vol_prep
          // Laboratory analysis row - wider spacing
          { x: 30, y: 320 },   // saa_analyse
          { x: 120, y: 320 },  // icp_analyse
          { x: 240, y: 320 },  // au_analyse
          { x: 330, y: 320 },  // vol_analyse
          // Validation row - wider spacing
          { x: 30, y: 420 },   // saa_validation
          { x: 120, y: 420 },  // icp_validation
          { x: 240, y: 420 },  // au_validation
          { x: 330, y: 420 },  // vol_validation
          // Final stages - centered
          { x: 180, y: 520 },  // certificate
          { x: 180, y: 600 },  // complete
        ]
      };
    } else if (isTablet) {
      // Tablet: Compact layout
      return {
        scale: 0.8,
        containerWidth: '100%',
        positions: [
          { x: 40, y: 150 },   // reception
          { x: 160, y: 150 },  // prep_mecanique
          { x: 280, y: 50 },   // saa_prep
          { x: 280, y: 120 },  // icp_prep
          { x: 280, y: 190 },  // au_prep
          { x: 280, y: 260 },  // vol_prep
          { x: 400, y: 50 },   // saa_analyse
          { x: 400, y: 120 },  // icp_analyse
          { x: 400, y: 190 },  // au_analyse
          { x: 400, y: 260 },  // vol_analyse
          { x: 520, y: 50 },   // saa_validation
          { x: 520, y: 120 },  // icp_validation
          { x: 520, y: 190 },  // au_validation
          { x: 520, y: 260 },  // vol_validation
          { x: 640, y: 120 },  // certificate
          { x: 640, y: 190 },  // complete
        ]
      };
    } else {
      // Desktop: Full layout
      return {
        scale: 1,
        containerWidth: '100%',
        positions: [
          { x: 50, y: 200 },   // reception
          { x: 220, y: 200 },  // prep_mecanique
          { x: 390, y: 40 },   // saa_prep
          { x: 390, y: 150 },  // icp_prep
          { x: 390, y: 260 },  // au_prep
          { x: 390, y: 370 },  // vol_prep
          { x: 560, y: 40 },   // saa_analyse
          { x: 560, y: 150 },  // icp_analyse
          { x: 560, y: 260 },  // au_analyse
          { x: 560, y: 370 },  // vol_analyse
          { x: 730, y: 40 },   // saa_validation
          { x: 730, y: 150 },  // icp_validation
          { x: 730, y: 260 },  // au_validation
          { x: 730, y: 370 },  // vol_validation
          { x: 900, y: 160 },  // certificate
          { x: 900, y: 270 },  // complete
        ]
      };
    }
  };

  const { scale, containerWidth, positions } = getResponsivePositions();

  // Define network nodes with responsive positioning - memoized to update when stats change
  const networkNodes: NetworkNodeData[] = useMemo(() => [
    {
      id: 'reception',
      name: 'Réception',
      shortName: isMobile ? 'Réception' : 'Réception\nÉchantillons',
      icon: <Inventory2 sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[0].x,
      y: positions[0].y,
      type: 'reception',
      count: stageCounts.reception ?? (stats?.reception || 0),
      connections: ['prep_mecanique'],
      description: 'Accueil et enregistrement des échantillons',
    },
    {
      id: 'prep_mecanique',
      name: 'Préparation Mécanique',
      shortName: isMobile ? 'Préparation' : 'Préparation\nMécanique',
      icon: <PrecisionManufacturing sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[1].x,
      y: positions[1].y,
      type: 'preparation',
      count: stageCounts.prep_mecanique ?? (stats?.preparation || 0),
      connections: ['saa_prep', 'icp_prep', 'au_prep', 'vol_prep'],
      description: 'Broyage, tamisage et homogénéisation',
    },
    {
      id: 'saa_prep',
      name: 'SAA Préparation',
      shortName: isMobile ? 'SAA Prep' : 'Préparation\nSAA',
      icon: <Psychology sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[2].x,
      y: positions[2].y,
      type: 'laboratory',
      laboratory: 'SAA',
      stage: 'PREP_CHIMIQUE',
      count: stageCounts.saa_prep ?? (stats?.saa_prep || 0),
      connections: ['saa_analyse'],
      description: 'Digestion SAA',
    },
    {
      id: 'icp_prep',
      name: 'ICP Préparation',
      shortName: isMobile ? 'ICP Prep' : 'Préparation\nICP',
      icon: <LocalPharmacy sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[3].x,
      y: positions[3].y,
      type: 'laboratory',
      laboratory: 'ICP',
      stage: 'PREP_CHIMIQUE',
      count: stageCounts.icp_prep ?? (stats?.icp_prep || 0),
      connections: ['icp_analyse'],
      description: 'Digestion ICP',
    },
    {
      id: 'au_prep',
      name: 'AU Préparation',
      shortName: isMobile ? 'AU Prep' : 'Préparation\nAU',
      icon: <Build sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[4].x,
      y: positions[4].y,
      type: 'laboratory',
      laboratory: 'AU',
      stage: 'PREP_CHIMIQUE',
      count: stageCounts.au_prep ?? (stats?.au_prep || 0),
      connections: ['au_analyse'],
      description: 'Digestion AU',
    },
    {
      id: 'vol_prep',
      name: 'VOL Préparation',
      shortName: isMobile ? 'VOL Prep' : 'Préparation\nVolumétrie',
      icon: <InvertColors sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[5].x,
      y: positions[5].y,
      type: 'laboratory',
      laboratory: 'VOLUMETRIE',
      stage: 'PREP_CHIMIQUE',
      count: stageCounts.vol_prep ?? (stats?.vol_prep || 0),
      connections: ['vol_analyse'],
      description: 'Préparation Volumétrie',
    },
    {
      id: 'saa_analyse',
      name: 'SAA Analyse',
      shortName: isMobile ? 'SAA Ana' : 'Analyse\nSAA',
      icon: <Whatshot sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[6].x,
      y: positions[6].y,
      type: 'laboratory',
      laboratory: 'SAA',
      stage: 'ANALYSE',
      count: stageCounts.saa_analyse ?? (stats?.saa_analyse || 0),
      connections: ['saa_validation'],
      description: 'Absorption atomique',
    },
    {
      id: 'icp_analyse',
      name: 'ICP Analyse',
      shortName: isMobile ? 'ICP Ana' : 'Analyse\nICP',
      icon: <AutoGraph sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[7].x,
      y: positions[7].y,
      type: 'laboratory',
      laboratory: 'ICP',
      stage: 'ANALYSE',
      count: stageCounts.icp_analyse ?? (stats?.icp_analyse || 0),
      connections: ['icp_validation'],
      description: 'ICP-MS/OES',
    },
    {
      id: 'au_analyse',
      name: 'AU Analyse',
      shortName: isMobile ? 'AU Ana' : 'Analyse\nAU',
      icon: <Diamond sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[8].x,
      y: positions[8].y,
      type: 'laboratory',
      laboratory: 'AU',
      stage: 'ANALYSE',
      count: stageCounts.au_analyse ?? (stats?.au_analyse || 0),
      connections: ['au_validation'],
      description: 'Coupellation',
    },
    {
      id: 'vol_analyse',
      name: 'VOL Analyse',
      shortName: isMobile ? 'VOL Ana' : 'Analyse\nVolumétrie',
      icon: <WaterDrop sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[9].x,
      y: positions[9].y,
      type: 'laboratory',
      laboratory: 'VOLUMETRIE',
      stage: 'ANALYSE',
      count: stageCounts.vol_analyse ?? (stats?.vol_analyse || 0),
      connections: ['vol_validation'],
      description: 'Titrage',
    },
    {
      id: 'saa_validation',
      name: 'SAA Validation',
      shortName: isMobile ? 'SAA Val' : 'Validation\nSAA',
      icon: <GppGood sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[10].x,
      y: positions[10].y,
      type: 'validation',
      laboratory: 'SAA',
      count: getValidationSamplesForLab('SAA'),
      connections: ['certificate'],
      description: 'Validation SAA',
    },
    {
      id: 'icp_validation',
      name: 'ICP Validation',
      shortName: isMobile ? 'ICP Val' : 'Validation\nICP',
      icon: <GppGood sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[11].x,
      y: positions[11].y,
      type: 'validation',
      laboratory: 'ICP',
      count: getValidationSamplesForLab('ICP'),
      connections: ['certificate'],
      description: 'Validation ICP',
    },
    {
      id: 'au_validation',
      name: 'AU Validation',
      shortName: isMobile ? 'AU Val' : 'Validation\nAU',
      icon: <GppGood sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[12].x,
      y: positions[12].y,
      type: 'validation',
      laboratory: 'AU',
      count: getValidationSamplesForLab('AU'),
      connections: ['certificate'],
      description: 'Validation AU',
    },
    {
      id: 'vol_validation',
      name: 'VOL Validation',
      shortName: isMobile ? 'VOL Val' : 'Validation\nVolumétrie',
      icon: <GppGood sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[13].x,
      y: positions[13].y,
      type: 'validation',
      laboratory: 'VOLUMETRIE',
      count: getValidationSamplesForLab('VOLUMETRIE'),
      connections: ['certificate'],
      description: 'Validation VOL',
    },
    {
      id: 'certificate',
      name: 'Certificat',
      shortName: isMobile ? 'Certificat' : 'Génération\nCertificat',
      icon: <MenuBook sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[14].x,
      y: positions[14].y,
      type: 'certificate',
      count: stageCounts.certificate ?? (stats?.certificate || 0),
      connections: ['complete'],
      description: 'Génération certificats',
    },
    {
      id: 'complete',
      name: 'Terminé',
      shortName: isMobile ? 'Terminé' : 'Échantillons\nTerminés',
      icon: <TaskAlt sx={{ fontSize: isMobile ? 14 : 16, color: '#fff' }} />,
      x: positions[15].x,
      y: positions[15].y,
      type: 'complete',
      count: stageCounts.complete ?? (stats?.completed || 0),
      connections: [],
      description: 'Échantillons prêts',
    },
  ], [stats, positions, stageCounts]);

  // Debug networkNodes counts
  useEffect(() => {
    if (stats) {
      console.log('🎯 Current stats:', stats);
      console.log('🏗️ NetworkNodes counts:');
      networkNodes.forEach(node => {
        if (node.count > 0) {
          console.log(`   ${node.id}: ${node.count} samples (${node.name})`);
        }
      });
      
      const totalNodeCounts = networkNodes.reduce((sum, node) => sum + node.count, 0);
      console.log(`📊 Total node counts: ${totalNodeCounts}, Total stats samples: ${stats.total_samples}`);
    }
  }, [stats, networkNodes]);

  // Animate active connections based on sample flow
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate active connections based on sample counts
      const activeNodes = networkNodes.filter(node => node.count > 0);
      const newActiveConnections = new Set<string>();
      
      activeNodes.forEach(node => {
        node.connections.forEach(connId => {
          newActiveConnections.add(`${node.id}-${connId}`);
        });
      });
      
      setActiveConnections(newActiveConnections);
    }, 2000);

    return () => clearInterval(interval);
  }, [stats]);

     const loadWorkflowData = useCallback(async () => {
     try {
       setLoading(true);
       const overview = await clientDashboardService.getDashboardOverview();
      
      const workflowStats: WorkflowStats = {
        total_samples: overview.analytics.total_samples,
        reception: 0,
        preparation: 0,
        saa_prep: 0,
        saa_analyse: 0,
        icp_prep: 0,
        icp_analyse: 0,
        au_prep: 0,
        au_analyse: 0,
        vol_prep: 0,
        vol_analyse: 0,
        validation: 0,
        certificate: 0,
        completed: 0,
      };

             // Map status distribution to workflow stats
       if (overview.analytics?.status_distribution) {
         overview.analytics.status_distribution.forEach((statusData: any) => {
           const status = statusData.status;
           const count = statusData.count;
          
          switch (status) {
            case 'RECEPTION':
              workflowStats.reception = count;
              break;
            case 'PREPARATION_MECANIQUE':
              workflowStats.preparation = count;
              break;
            case 'LABORATOIRE_SAA_PREP_CHIMIQUE':
              workflowStats.saa_prep = count;
              break;
            case 'LABORATOIRE_SAA_ANALYSE':
              workflowStats.saa_analyse = count;
              break;
            case 'LABORATOIRE_ICP_PREP_CHIMIQUE':
              workflowStats.icp_prep = count;
              break;
            case 'LABORATOIRE_ICP_ANALYSE':
              workflowStats.icp_analyse = count;
              break;
            case 'LABORATOIRE_AU_PREP_CHIMIQUE':
              workflowStats.au_prep = count;
              break;
            case 'LABORATOIRE_AU_ANALYSE':
              workflowStats.au_analyse = count;
              break;
            case 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE':
              workflowStats.vol_prep = count;
              break;
            case 'LABORATOIRE_VOLUMETRIE_ANALYSE':
              workflowStats.vol_analyse = count;
              break;
            case 'VALIDATION_SERVICE':
              workflowStats.validation = count;
              break;
            case 'GENERATION_CERTIFICAT':
              workflowStats.certificate = count;
              break;
            case 'PRET':
              workflowStats.completed = count;
              break;
                         default:
               break;
           }
         });
       }
      setStats(workflowStats);
      
      // Load actual counts for ALL stages (not just validation)
      await loadStageCounts();
      
      // Load actual validation counts using same logic as supervisor dashboards
      await loadActualValidationCounts();
    } catch (error) {
      console.error('Error loading workflow data:', error);
      setError('Erreur lors du chargement des données de workflow');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load workflow data on mount and set up refresh interval
  useEffect(() => {
    loadWorkflowData();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadWorkflowData, 30000);
    return () => clearInterval(interval);
  }, [loadWorkflowData]);

     const handleNodeClick = useCallback(async (node: NetworkNodeData) => {
     try {

      setSelectedNode(node);
      setLoadingSamples(true);
      setDialogOpen(true);

      // Map node.id to status filter
      const statusMap: { [key: string]: string } = {
        'reception': 'RECEPTION',
        'prep_mecanique': 'PREPARATION_MECANIQUE',
        'saa_prep': 'LABORATOIRE_SAA_PREP_CHIMIQUE',
        'saa_analyse': 'LABORATOIRE_SAA_ANALYSE',
        'icp_prep': 'LABORATOIRE_ICP_PREP_CHIMIQUE',
        'icp_analyse': 'LABORATOIRE_ICP_ANALYSE',
        'au_prep': 'LABORATOIRE_AU_PREP_CHIMIQUE',
        'au_analyse': 'LABORATOIRE_AU_ANALYSE',
        'vol_prep': 'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE',
        'vol_analyse': 'LABORATOIRE_VOLUMETRIE_ANALYSE',
        'saa_validation': 'VALIDATION_SERVICE',
        'icp_validation': 'VALIDATION_SERVICE',
        'au_validation': 'VALIDATION_SERVICE',
        'vol_validation': 'VALIDATION_SERVICE',
        'certificate': 'GENERATION_CERTIFICAT',
        'complete': 'PRET'
      };

             // Load samples for this stage
       const status = statusMap[node.id];
       
       if (status) {
         // 🔥 CRITICAL BGM FIX: For validation nodes, must fetch from multiple statuses like Admin Dashboard
         let finalStatus = status;
         let sampleLimit = 500;
         
         let response;
         
         if (node.type === 'validation' && (node.laboratory === 'AU' || node.laboratory === 'ICP')) {
           // For AU/ICP validation, fetch from multiple statuses to get BGM samples
           // Note: clientDashboardService doesn't support array status, so we fetch multiple calls
           
           const extraFilters: any = {};
           if (node.laboratory === 'AU') {
             extraFilters.laboratory = 'LABORATOIRE_AU';
             sampleLimit = 2000; // For AU validation, fetch more samples to ensure complete batches
           }
           
           // Fetch from all relevant statuses
           const validationResponse = await clientDashboardService.getClientSamples(1, sampleLimit, { 
             status: SampleStatus.VALIDATION_SERVICE, 
             ...extraFilters 
           });
           
           const icpPrepResponse = await clientDashboardService.getClientSamples(1, sampleLimit, { 
             status: SampleStatus.LABORATOIRE_ICP_PREP_CHIMIQUE, 
             ...extraFilters 
           });
           
           const icpAnalyseResponse = await clientDashboardService.getClientSamples(1, sampleLimit, { 
             status: SampleStatus.LABORATOIRE_ICP_ANALYSE, 
             ...extraFilters 
           });
           
           // Combine all responses
           const allSamples = [
             ...(validationResponse.samples || []),
             ...(icpPrepResponse.samples || []),
             ...(icpAnalyseResponse.samples || [])
           ];
           
           response = { samples: allSamples };
         } else {
           const extraFilters: any = {};
           if (node.laboratory === 'AU') {
             extraFilters.laboratory = 'LABORATOIRE_AU';
           }
           
           response = await clientDashboardService.getClientSamples(1, sampleLimit, { 
             status: finalStatus, 
             ...extraFilters 
           });
         }
        
        // Map SampleTrackingData to ClientSample interface (preserve metadata for batch info)
        let clientSamples: ClientSample[] = response.samples.map(sample => ({
          id: sample.id,
          sample_id: sample.sample_id,
          external_id: sample.external_id, // Include client reference
          client_name: sample.nom_projet || 'Projet non spécifié',
          status: sample.statut,
          priority: sample.priorite,
          progress_percentage: sample.progress_percentage,
          workflow_duration_days: sample.duree_workflow_jours,
          assigned_technician: undefined, // Not available in SampleTrackingData
          current_location: sample.current_location,
          metadonnees: sample.metadonnees, // Preserve metadata for batch information
        } as any));
        
        // 🎯 FRONTEND FILTERING: For validation nodes, apply EXACT same logic as Admin Dashboard
        if (node.type === 'validation' && node.laboratory) {
          clientSamples = clientSamples.filter((sample: any) => {
            const isBGMSample = sample.metadonnees?.bgm_method_selected === true;
            
            if (node.laboratory === 'SAA') {
              // SAA validation logic (same as Admin Dashboard)
              if (sample.statut === SampleStatus.VALIDATION_SERVICE && 
                  sample.metadonnees?.saa_analysis_form && 
                  !isBGMSample) {
                return true;
              }
            } else if (node.laboratory === 'ICP') {
              // ICP validation logic (same as Admin Dashboard)
              if (isBGMSample) {
                const icpAnalysisComplete = sample.metadonnees?.icp_analysis_completed === true;
                const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
                const auValidationComplete = sample.metadonnees?.au_validation_completed === true;
                // 🔥 FIX: For BGM samples from ICP workflow statuses, don't require VALIDATION_SERVICE
                if (icpAnalysisComplete &&
                    (!icpValidationComplete || (icpValidationComplete && !auValidationComplete))) {
                  return true;
                }
              } else {
                const hasIcpAnalysis = sample.metadonnees?.icp_analysis_form || sample.metadonnees?.icp_analysis_data;
                const icpValidationCompleted = sample.metadonnees?.icp_validation_completed;
                if (sample.statut === SampleStatus.VALIDATION_SERVICE &&
                    hasIcpAnalysis &&
                    !icpValidationCompleted) {
                  return true;
                }
              }
            } else if (node.laboratory === 'AU') {
              // AU validation logic (same as Admin Dashboard)
              if (isBGMSample) {
                const auAnalysisComplete = sample.metadonnees?.au_analysis_completed === true;
                const auValidationComplete = sample.metadonnees?.au_validation_completed === true;
                const icpValidationComplete = sample.metadonnees?.icp_validation_completed === true;
                if (auAnalysisComplete && (!auValidationComplete || (auValidationComplete && !icpValidationComplete))) {
                  return true;
                }
              } else {
                if (sample.statut === SampleStatus.VALIDATION_SERVICE &&
                    sample.metadonnees?.au_analysis_form && 
                    !isBGMSample) {
                  return true;
                }
              }
            } else if (node.laboratory === 'VOLUMETRIE') {
              // VOLUMETRIE validation logic (same as Admin Dashboard)
              if (sample.statut === SampleStatus.VALIDATION_SERVICE &&
                  sample.metadonnees?.volumetrie_analysis_form &&
                  !isBGMSample) {
                return true;
              }
            }
            
            return false;
          });
        }
        
        setStageSamples(clientSamples);
        
        // Call onStageClick with both stageId and samples
        if (onStageClick) {
          onStageClick(node.id, clientSamples);
        }
      } else {
        setStageSamples([]);
        
        // Call onStageClick with empty samples array
        if (onStageClick) {
          onStageClick(node.id, []);
        }
      }
    } catch (error) {
      console.error('Error loading node samples:', error);
      setStageSamples([]);
    } finally {
      setLoadingSamples(false);
    }
  }, [onStageClick]);

  const renderSankeyFlows = () => {
    return networkNodes.map(node => 
      node.connections.map(targetId => {
        const target = networkNodes.find(n => n.id === targetId);
        if (!target) return null;

        const connectionId = `${node.id}-${targetId}`;
        const isActive = activeConnections.has(connectionId);
        const flowThickness = Math.max(0.05, Math.min(node.count / 15, 1));

        // Create curved path for Sankey flow
        const startX = node.x + (isMobile ? 40 : 60);
        const startY = node.y + (isMobile ? 25 : 35);
        const endX = target.x + (isMobile ? 40 : 60);
        const endY = target.y + (isMobile ? 25 : 35);
        
        const controlX1 = startX + (endX - startX) * 0.3;
        const controlY1 = startY;
        const controlX2 = endX - (endX - startX) * 0.3;
        const controlY2 = endY;

        const pathData = `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`;

        return (
          <SankeyFlow key={connectionId} active={isActive} thickness={flowThickness}>
            <defs>
              <linearGradient id={`gradient-${connectionId}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={alpha(theme.palette.primary.main, 0.8)} />
                <stop offset="30%" stopColor={alpha(theme.palette.secondary.main, 0.9)} />
                <stop offset="70%" stopColor={alpha(theme.palette.success.main, 0.8)} />
                <stop offset="100%" stopColor={alpha(theme.palette.info.main, 0.6)} />
              </linearGradient>
              {isActive && (
                <filter id={`glow-${connectionId}`}>
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              )}
            </defs>
            <path
              className="flow-path"
              d={pathData}
              stroke={`url(#gradient-${connectionId})`}
              filter={isActive ? `url(#glow-${connectionId})` : 'none'}
              strokeDasharray={isActive ? '10 5' : 'none'}
            />
          </SankeyFlow>
        );
      })
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="500px">
        <CircularProgress />
      </Box>
    );
  }



  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Slide direction="up" in mountOnEnter>
        <Card sx={{ mb: 4, overflow: 'hidden' }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: isMobile ? 1 : 4, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Timeline sx={{ fontSize: isMobile ? 24 : 28, color: 'primary.main' }} />
                {isMobile ? "Flux Laboratoire AFRILAB" : "Diagramme de Flux Sankey - Laboratoires AFRILAB"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isMobile ? "Suivi vertical du flux d'échantillons" : "Visualisation du flux d'échantillons • L'épaisseur des connexions représente le volume des échantillons"}
              </Typography>
              {!isMobile && (
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Inventory2 sx={{ fontSize: 12 }} /> Réception  
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Whatshot sx={{ fontSize: 12 }} /> SAA (Flamme)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AutoGraph sx={{ fontSize: 12 }} /> ICP (Spectro)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Diamond sx={{ fontSize: 12 }} /> AU (Or)
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WaterDrop sx={{ fontSize: 12 }} /> Volumétrie
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GppGood sx={{ fontSize: 12 }} /> Validation
                  </Typography>
                </Box>
              )}
            </Box>
            
            <SankeyContainer ref={sankeyRef}>
              {renderSankeyFlows()}
              
              {networkNodes.map((node) => {
                
                
                return (
                  <SampleCountBadge
                    key={node.id}
                    badgeContent={node.count > 0 ? node.count : null}
                    color="error"
                    overlap="rectangular"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <InteractiveCard
                      active={node.count > 0}
                      nodeType={node.type}
                      laboratory={node.laboratory}
                      sampleCount={node.count}
                      isMobile={isMobile}
                      onClick={(e) => {
                         e.preventDefault();
                         e.stopPropagation();
                         handleNodeClick(node);
                       }}
                      sx={{
                        left: node.x,
                        top: node.y,
                      }}
                    >
                      {/* Laboratory Icon */}
                      {node.icon}
                      
                      {/* Laboratory Name */}
                      <Typography 
                        variant="caption" 
                        color="white" 
                        fontWeight={700}
                        fontSize={isMobile ? "8px" : "9px"}
                        lineHeight={1}
                        textAlign="center"
                        sx={{ 
                          whiteSpace: 'pre-line',
                          textShadow: '0 1px 2px rgba(0,0,0,0.7)',
                          maxWidth: isMobile ? '70px' : '110px',
                          mt: 0.5,
                        }}
                      >
                        {node.shortName}
                      </Typography>
                      
                      {/* Direct count display as fallback */}
                      {node.count > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: '#ff1744',
                            color: 'white',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            zIndex: 10000,
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                          }}
                        >
                          {node.count}
                        </Box>
                      )}
                    </InteractiveCard>
                  </SampleCountBadge>
                );
              })}
              
              {/* Flow direction indicators */}
              {!isMobile && (
                <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    padding: 1,
                    borderRadius: 1
                  }}>
                    <Timeline sx={{ fontSize: 16 }} />
                    Flux des échantillons • Épaisseur = Volume
                  </Typography>
                </Box>
              )}
            </SankeyContainer>
          </CardContent>
        </Card>
      </Slide>

      {/* Sample Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {selectedNode?.icon}
            <Box>
              <Typography variant={isMobile ? "h6" : "h6"}>
                {selectedNode?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedNode?.description}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingSamples ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : stageSamples.length > 0 ? (
            <>
              {/* Show note if we're displaying a subset of samples */}
              {stageSamples.length >= 500 && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1 }}>
                  <Typography variant="body2" color="info.main">
                    ℹ️ Affichage limité aux 500 premiers échantillons pour des raisons de performance. 
                    Le nombre total peut être plus élevé.
                  </Typography>
                </Box>
              )}
              <TableContainer component={Paper}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell>Échantillon / Lot</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Priorité</TableCell>
                    <TableCell>Progression</TableCell>
                    {!isMobile && <TableCell>Durée</TableCell>}
                    {!isMobile && <TableCell>Technicien</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // Group samples by batch (like admin dashboards)
                    const { batches, individualSamples } = groupSamplesByBatch(stageSamples);
                    const items: Array<{type: 'batch', batchNumber: string, samples: ClientSample[]} | {type: 'individual', sample: ClientSample}> = [];
                    
                    // Add batch items
                    Object.entries(batches).forEach(([batchNumber, batchSamples]) => {
                      items.push({ type: 'batch', batchNumber, samples: batchSamples });
                    });
                    
                    // Add individual items
                    individualSamples.forEach(sample => {
                      items.push({ type: 'individual', sample });
                    });
                    
                    console.log('🎯 Rendering samples with batches:', { 
                      batchCount: Object.keys(batches).length, 
                      individualCount: individualSamples.length,
                      totalItems: items.length 
                    });
                    
                    return items.map((item, index) => {
                      if (item.type === 'batch') {
                        const batchItem = item as {type: 'batch', batchNumber: string, samples: ClientSample[]};
                        const isExpanded = expandedBatches.has(batchItem.batchNumber);
                        
                        return (
                          <React.Fragment key={`batch-${batchItem.batchNumber}`}>
                            {/* Batch Header Row */}
                            <TableRow 
                              onClick={() => toggleBatchExpansion(batchItem.batchNumber)}
                              sx={{ 
                                cursor: 'pointer', 
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.15) }
                              }}
                            >
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="body2" fontWeight="bold">
                                        Lot {batchItem.batchNumber}
                                      </Typography>
                                      <Chip 
                                        label={`${batchItem.samples.length} échantillons`} 
                                        size="small" 
                                        color="primary" 
                                        variant="outlined"
                                      />
                                    </Box>
                                    {/* Client Reference Information */}
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="caption" color="text.secondary">
                                        Lab Refs: {batchItem.samples.map(s => s.sample_id).join(', ')}
                                      </Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="caption" color="text.secondary">
                                        Client Refs: {batchItem.samples.map(s => s.external_id || 'N/A').join(', ')}
                                      </Typography>
                                    </Box>
                                    {batchItem.samples[0]?.metadonnees?.batch_info && (
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="text.secondary">
                                          Batch Info: Pos {batchItem.samples[0].metadonnees.batch_info.sample_position || 1} / 
                                          {batchItem.samples[0].metadonnees.batch_info.total_samples || batchItem.samples.length}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label="Lot" color="info" size="small" />
                              </TableCell>
                              <TableCell>{batchItem.samples[0]?.client_name || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={batchItem.samples[0]?.priority || 'NORMALE'}
                                  color={getPriorityColor(batchItem.samples[0]?.priority || 'NORMALE')}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={Math.round(batchItem.samples.reduce((sum, s) => sum + s.progress_percentage, 0) / batchItem.samples.length)}
                                    sx={{ width: isMobile ? 40 : 60 }}
                                  />
                                  <Typography variant="caption">
                                    {Math.round(batchItem.samples.reduce((sum, s) => sum + s.progress_percentage, 0) / batchItem.samples.length)}%
                                  </Typography>
                                </Box>
                              </TableCell>
                              {!isMobile && (
                                <TableCell>
                                  {Math.round(batchItem.samples.reduce((sum, s) => sum + s.workflow_duration_days, 0) / batchItem.samples.length)} jours
                                </TableCell>
                              )}
                              {!isMobile && (
                                <TableCell>Lot</TableCell>
                              )}
                            </TableRow>
                            
                            {/* Expanded Batch Samples */}
                            {isExpanded && batchItem.samples.map((sample) => (
                              <TableRow key={sample.id} sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell sx={{ pl: 4 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      Lab: {sample.sample_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Client: {sample.external_id || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip label="Échantillon" size="small" />
                                </TableCell>
                                <TableCell>{sample.client_name}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={sample.priority}
                                    color={getPriorityColor(sample.priority)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={sample.progress_percentage}
                                      sx={{ width: isMobile ? 40 : 60 }}
                                    />
                                    <Typography variant="caption">
                                      {sample.progress_percentage}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                                {!isMobile && (
                                  <TableCell>
                                    {sample.workflow_duration_days} jours
                                  </TableCell>
                                )}
                                {!isMobile && (
                                  <TableCell>
                                    {sample.assigned_technician || 'Non assigné'}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      } else {
                        // Individual sample
                        const sample = (item as {type: 'individual', sample: ClientSample}).sample;
                        return (
                          <TableRow key={sample.id}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  Lab: {sample.sample_id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Client: {sample.external_id || 'N/A'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label="Individuel" size="small" />
                            </TableCell>
                            <TableCell>{sample.client_name}</TableCell>
                            <TableCell>
                              <Chip 
                                label={sample.priority}
                                color={getPriorityColor(sample.priority)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <LinearProgress 
                                  variant="determinate" 
                                  value={sample.progress_percentage}
                                  sx={{ width: isMobile ? 40 : 60 }}
                                />
                                <Typography variant="caption">
                                  {sample.progress_percentage}%
                                </Typography>
                              </Box>
                            </TableCell>
                            {!isMobile && (
                              <TableCell>
                                {sample.workflow_duration_days} jours
                              </TableCell>
                            )}
                            {!isMobile && (
                              <TableCell>
                                {sample.assigned_technician || 'Non assigné'}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          ) : (
            <Typography>
              Aucun échantillon trouvé pour cette étape.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientWorkflowVisualization; 